import { createContext, Dispatch, MutableRefObject, Reducer, useContext, useEffect, useReducer } from 'react';
import { MandatoryChildren } from '../../../models/children-props';
import {
  useCompleteOnboardingMutation,
  useGetCategoriesProfilesWithBoardsQuery,
  UserOrganizationRole
} from '../../../models/graphql';
import { DEMO_CATEGORY_NAME, getCongratsKey, ProgresStepType } from '../constants/content';
import { OnboardingWidget } from '../OnboardingWidget';
import { OnboardingActions } from './Onboarding.actions';
import { OnboardingReducer } from './Onboarding.reducer';
import { GetMe_me } from '../../../models/legacy-graphql';
import { getOnboardingSteps, getStringifiedStepRoutes } from '../helpers/onboardingGetters';
import { isOnboardingVersionProperGuard } from '../helpers/guards';
import { useLocation } from 'react-router-dom';
import { isPostEditorActive } from '../helpers/isPostEditorActive';
import { AppContext } from '../../../providers/AppProvider';
import { DeepReadonly } from '../../../models/basic-objects';
import { mapProfileFragmentToSelectedSocialProfileValue } from '../../PostEditor/helpers/mapProfileFragmentToSelectedSocialProfileValue';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';

export type OnboardingContextType = {
  state: OnboardingStateType;
  dispatch: Dispatch<OnboardingActions>;
  showOnboarding: boolean;
  showTourTips: boolean;
};

export type OnboardingVersionType = 1 | 2 | 3;

export type OnboardingStateType = DeepReadonly<{
  version: OnboardingVersionType;
  stepsCompleted: boolean;
  isCongrats: boolean;
  progress: ProgresStepType[];
  ref: MutableRefObject<HTMLDivElement | null>;
  expanded: boolean;
  activeStepIndex: number;
  demoCategoryId: string;
  selectedProfiles: SelectedSocialProfileValue[];
}>;

const InitialOnboardingValue: OnboardingContextType = {
  state: {
    version: 1,
    stepsCompleted: false,
    isCongrats: false,
    progress: [],
    ref: { current: null },
    expanded: true,
    activeStepIndex: -1,
    demoCategoryId: '',
    selectedProfiles: []
  },
  dispatch: () => {
    /** **/
  },
  showOnboarding: true,
  showTourTips: true
};

export const OnboardingContext = createContext<OnboardingContextType>(InitialOnboardingValue);

type OnboardingProviderProps = MandatoryChildren<{
  me: GetMe_me;
  selectedWorkspaceId: string;
  selectedOrgRole: UserOrganizationRole;
}>;

export const OnboardingProvider = ({
  children,
  me,
  selectedOrgRole,
  selectedWorkspaceId
}: OnboardingProviderProps): JSX.Element => {
  const { postEditorContext } = useContext(AppContext);
  const { onboarding } = me;
  const { completed: onboardingCompleted, stepsSeen, version } = onboarding;
  const [_completeMutation] = useCompleteOnboardingMutation();
  const { pathname: currentRoute } = useLocation();
  const isAdmin = selectedOrgRole === UserOrganizationRole.OWNER;
  let showOnboarding = true;
  let showTourTips = true;
  const postEditorActive = isPostEditorActive(postEditorContext);
  const { data: categoriesProfiles } = useGetCategoriesProfilesWithBoardsQuery({
    variables: { workspaceId: selectedWorkspaceId }
  });

  if (!isOnboardingVersionProperGuard(version)) {
    throw new Error(`Onboarding Version has invalid value: ${version}`);
  }

  const isCongrats = !!stepsSeen.find(step => step.name === getCongratsKey(version));

  const onboardingSteps: ProgresStepType[] = getOnboardingSteps(version, stepsSeen);
  const stringifiedStepRoutes = getStringifiedStepRoutes(version);

  if (!isOnboardingVersionProperGuard(version) || !isAdmin) {
    showTourTips = false;
    showOnboarding = false;
  }

  if (onboardingCompleted) {
    showOnboarding = false;
  }

  // set the active step
  const firstUncompletedStep = onboardingSteps.findIndex(step => !step.completed);
  const activeStepIndex = firstUncompletedStep - 1 >= 0 ? firstUncompletedStep - 1 : 0;
  const stepsCompleted = !onboardingSteps.some(step => !step.completed);

  const initialState: OnboardingStateType = {
    demoCategoryId: '',
    selectedProfiles: [],
    progress: onboardingSteps,
    version,
    stepsCompleted,
    ref: { current: null },
    expanded: true,
    activeStepIndex,
    isCongrats
  };

  const [state, dispatch] = useReducer<Reducer<OnboardingStateType, OnboardingActions>>(
    OnboardingReducer,
    initialState
  );

  useEffect(() => {
    const keys = stepsSeen.map(step => step.name);
    dispatch({ type: 'setSeenSteps', payload: keys });
  }, [stepsSeen]);

  // we are not on any of the step routes, so unset the activeStepIndex
  useEffect(() => {
    const routes = onboardingSteps.flatMap(step => (step.route ? step.route : []));
    if (!routes.includes(currentRoute) && !postEditorActive && state.activeStepIndex > -1) {
      dispatch({ type: 'setActiveStepIndex', payload: -1 });
    }
  }, [onboardingSteps, currentRoute, state.activeStepIndex, postEditorActive]);

  // we are not on any of the step routes, so unset the activeStepIndex
  useEffect(() => {
    const routes = stringifiedStepRoutes.split(',');
    if (!routes.includes(currentRoute) && !postEditorActive && state.activeStepIndex > -1) {
      dispatch({ type: 'setActiveStepIndex', payload: -1 });
    } else if (routes.includes(currentRoute) || postEditorActive) {
      const routeToFind = postEditorActive ? '' : currentRoute;
      const activeIndex = routes.findIndex(route => route === routeToFind);
      dispatch({ type: 'setActiveStepIndex', payload: activeIndex });
    }
  }, [stringifiedStepRoutes, currentRoute, state.activeStepIndex, postEditorActive]);

  useEffect(() => {
    if (isCongrats) {
      dispatch({ type: 'completeTour' });
    }
  }, [isCongrats]);

  useEffect(() => {
    if (categoriesProfiles) {
      const { categories, socialProfiles } = categoriesProfiles.workspace;
      const demoCategory = categories.find(c => c.name === DEMO_CATEGORY_NAME && c.onboardingContent);
      const selectedProfiles = socialProfiles.filter(profile => profile.onboardingContent);
      const selectedSocialProfiles = mapProfileFragmentToSelectedSocialProfileValue(selectedProfiles);
      if (demoCategory && selectedSocialProfiles.length) {
        dispatch({
          type: 'setDemoCategoryAndProfiles',
          payload: { categoryId: demoCategory.id, profiles: selectedSocialProfiles }
        });
      }
    }
  }, [categoriesProfiles]);

  return (
    <OnboardingContext.Provider value={{ state, dispatch, showOnboarding, showTourTips }}>
      {children}
      {showOnboarding && categoriesProfiles && <OnboardingWidget />}
    </OnboardingContext.Provider>
  );
};
